import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import './HiLoCards.css';

// Contract ABIs
const HILOCARDS_ABI = [
  "function startGame(uint256 betAmount) external",
  "function makePrediction(uint8 prediction) external",
  "function cashOut() external",
  "function getActiveSession(address player) external view returns (tuple(address player, uint256 initialBet, uint256 currentWinnings, uint8 currentCard, uint8 streak, bool active, uint256 startTime))",
  "function getGameStats() external view returns (uint256 totalGames, uint256 totalBets, uint256 totalPayoutsAmount, uint256 houseProfit, uint256 companyTotal, uint256 routerTotal)",
  "function getRecentGames(uint256 count) external view returns (tuple(address player, uint256 betAmount, uint8 initialCard, uint8 finalCard, uint8 prediction, uint8 streak, uint256 grossPayout, uint256 netPayout, uint256 timestamp, bool won, bool cashedOut)[])",
  "function getGameConfig() external view returns (tuple(uint256 noWinChance, uint256 correctChance, uint256 correctPayout, uint256 maxStreak, uint256 streakMultiplier))",
  "function calculateHouseEdge() external view returns (uint256)",
  "function getEffectiveMultiplier() external view returns (uint256)",
  "function getCardName(uint8 cardNumber) external view returns (string)",
  "function owner() external view returns (address)",
  "function admin() external view returns (address)", 
  "function paused() external view returns (bool)",
  "function houseBalance() external view returns (uint256)",
  "function minBet() external view returns (uint256)",
  "function maxBet() external view returns (uint256)",
  "function pauseGame() external",
  "function unpauseGame() external", 
  "function fundHouse(uint256 amount) external",
  "function withdrawHouse(uint256 amount) external",
  "function setBetLimits(uint256 minBetAmount, uint256 maxBetAmount) external",
  "function updateWinChances(uint256 noWin, uint256 correct) external",
  "function updatePayouts(uint256 correctPayout, uint256 maxStreak, uint256 streakMultiplier) external",
  "event GameStarted(address indexed player, uint256 betAmount, uint8 initialCard)",
  "event PredictionMade(address indexed player, uint8 prediction, uint8 newCard, bool correct, uint8 streak)",
  "event GameCashedOut(address indexed player, uint256 grossPayout, uint256 netPayout)",
  "event GameEnded(address indexed player, bool won, uint256 finalPayout)"
];

const BLB_TOKEN_ABI = [
  "function balanceOf(address owner) external view returns (uint256)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function approve(address spender, uint256 amount) external returns (bool)"
];

// Contract Addresses
const CONTRACT_ADDRESSES = {
  HILOCARDS: "0x853b7c6750E0cBAbb377432f02a4E9700589d564", // Replace with actual address
  BLB_TOKEN: "0x9015e335f28cbad84EB255d5205CDb51B08A1B70"
};

// Card configurations with quantum energy theme
const CARD_NAMES = [
  'ACE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN',
  'EIGHT', 'NINE', 'TEN', 'JACK', 'QUEEN', 'KING'
];

const CARD_SYMBOLS = [
  '⚡', '⚡⚡', '⚡⚡⚡', '🔋', '🔋🔋', '🔋🔋🔋', '⚛️',
  '⚛️⚛️', '⚛️⚛️⚛️', '🌟', '👑', '💎', '🔥'
];

const PREDICTION_TYPES = {
  LOWER: { id: 0, name: 'ENERGY DECAY', icon: '⬇️', color: '#8000ff', description: 'Next cell has lower energy' },
  HIGHER: { id: 1, name: 'ENERGY SURGE', icon: '⬆️', color: '#ff6600', description: 'Next cell has higher energy' }
};

// 🎵 QUANTUM REACTOR SOUND SYSTEM 🎵
const playQuantumSound = (type, volume = 0.3) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const createTone = (frequency, duration, waveType = 'sine') => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      oscillator.type = waveType;
      
      gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    };

    const createComplexSound = (notes, duration) => {
      notes.forEach((note, index) => {
        setTimeout(() => {
          createTone(note.freq, note.duration || duration, note.wave || 'sine');
        }, note.delay || index * 50);
      });
    };

    switch (type) {
      case 'reactorHum':
        createComplexSound([
          { freq: 80, duration: 2, wave: 'sine', delay: 0 },
          { freq: 120, duration: 2, wave: 'triangle', delay: 100 },
          { freq: 160, duration: 2, wave: 'sine', delay: 200 }
        ], 1);
        break;
        
      case 'energySurge':
        createComplexSound([
          { freq: 200, duration: 0.3, wave: 'sawtooth', delay: 0 },
          { freq: 400, duration: 0.3, wave: 'triangle', delay: 50 },
          { freq: 600, duration: 0.4, wave: 'sine', delay: 100 },
          { freq: 800, duration: 0.5, wave: 'sine', delay: 150 }
        ], 0.3);
        break;
        
      case 'energyDecay':
        createComplexSound([
          { freq: 800, duration: 0.3, wave: 'triangle', delay: 0 },
          { freq: 600, duration: 0.3, wave: 'sawtooth', delay: 50 },
          { freq: 400, duration: 0.4, wave: 'sine', delay: 100 },
          { freq: 200, duration: 0.5, wave: 'sine', delay: 150 }
        ], 0.3);
        break;
        
      case 'fusionSuccess':
        createComplexSound([
          { freq: 523, duration: 0.4, wave: 'triangle', delay: 0 },
          { freq: 659, duration: 0.4, wave: 'triangle', delay: 100 },
          { freq: 784, duration: 0.4, wave: 'triangle', delay: 200 },
          { freq: 1047, duration: 0.6, wave: 'sine', delay: 300 }
        ], 0.4);
        break;
        
      case 'coreMeltdown':
        createComplexSound([
          { freq: 1000, duration: 0.2, wave: 'sawtooth', delay: 0 },
          { freq: 800, duration: 0.2, wave: 'square', delay: 100 },
          { freq: 600, duration: 0.3, wave: 'triangle', delay: 200 },
          { freq: 300, duration: 0.4, wave: 'sawtooth', delay: 300 },
          { freq: 150, duration: 0.5, wave: 'square', delay: 400 }
        ], 0.25);
        break;
        
      case 'quantumClick':
        createComplexSound([
          { freq: 1500, duration: 0.05, wave: 'square', delay: 0 },
          { freq: 1800, duration: 0.05, wave: 'triangle', delay: 20 }
        ], 0.05);
        break;
        
      case 'reactorOverload':
        createComplexSound([
          { freq: 100, duration: 1, wave: 'sawtooth', delay: 0 },
          { freq: 200, duration: 1, wave: 'square', delay: 100 },
          { freq: 400, duration: 1, wave: 'triangle', delay: 200 },
          { freq: 800, duration: 1, wave: 'sine', delay: 300 },
          { freq: 1600, duration: 1, wave: 'sine', delay: 400 }
        ], 0.8);
        break;
        
      case 'quantumHover':
        createTone(1200, 0.1, 'triangle');
        break;
        
      default:
        createTone(800, 0.1);
    }
  } catch (error) {
    console.log('Audio not available:', error);
  }
};

const HiLoCards = ({ walletData, onBack }) => {
  const [gameState, setGameState] = useState({
    betAmount: '10',
    isPlaying: false,
    isApproving: false,
    session: null,
    balance: '0',
    allowance: '0',
    playerLevel: 1,
    experience: 0,
    totalWins: 0,
    showFusionPopup: false,
    showPredictionResult: false,
    reactorPower: 0
  });

  const [gameStats, setGameStats] = useState({
    totalGames: 0,
    totalBets: '0',
    totalPayouts: '0',
    houseEdge: '5',
    effectiveMultiplier: '1.90',
    playersOnline: 0
  });

  const [gameHistory, setGameHistory] = useState([]);
  const [animationState, setAnimationState] = useState('idle');
  const [predictionResult, setPredictionResult] = useState(null);
  const [energyParticles, setEnergyParticles] = useState([]);
  const [connected, setConnected] = useState(!!walletData?.account);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [contracts, setContracts] = useState({
    hiLoCards: null,
    blbToken: null
  });
  
  // Admin Panel State
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminState, setAdminState] = useState({
    houseBalance: '0',
    isPaused: false,
    fundAmount: '',
    withdrawAmount: '',
    minBet: '1',
    maxBet: '1000000',
    newWinChances: {
      noWin: '50.0',
      correct: '20.0'
    },
    newPayouts: {
      correctPayout: '190',
      maxStreak: '5',
      streakMultiplier: '150'
    }
  });

  const reactorRef = useRef(null);

  const playGameSound = (type, volume = 0.3) => {
    if (soundEnabled) {
      playQuantumSound(type, volume);
    }
  };

  // Energy particles effect
  useEffect(() => {
    const generateEnergyParticles = () => {
      const particles = [];
      const particleCount = gameState.session?.streak ? Math.min(gameState.session.streak * 10, 50) : 20;
      
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 4 + 2,
          speed: Math.random() * 2 + 1,
          opacity: Math.random() * 0.8 + 0.2,
          color: gameState.session?.streak > 2 ? '#ff6600' : '#0080ff'
        });
      }
      setEnergyParticles(particles);
    };

    generateEnergyParticles();
    
    const interval = setInterval(() => {
      setEnergyParticles(prev => prev.map(particle => ({
        ...particle,
        x: (particle.x + particle.speed * 0.5) % 100,
        y: (particle.y + particle.speed * 0.3) % 100,
        opacity: 0.2 + Math.sin(Date.now() * 0.001 + particle.id) * 0.3
      })));
    }, 100);

    return () => clearInterval(interval);
  }, [gameState.session?.streak]);

  // Initialize contracts
  useEffect(() => {
    if (walletData?.provider && walletData?.account) {
      initializeContracts();
    } else {
      setContracts({ hiLoCards: null, blbToken: null });
    }
  }, [walletData?.provider, walletData?.account]);

  const initializeContracts = async () => {
    try {
      const signer = walletData.provider.getSigner();
      
      const hiLoCardsContract = new ethers.Contract(
        CONTRACT_ADDRESSES.HILOCARDS,
        HILOCARDS_ABI,
        signer
      );
      
      const blbTokenContract = new ethers.Contract(
        CONTRACT_ADDRESSES.BLB_TOKEN,
        BLB_TOKEN_ABI,
        signer
      );

      setContracts({
        hiLoCards: hiLoCardsContract,
        blbToken: blbTokenContract
      });

      setTimeout(() => {
        loadGameData(hiLoCardsContract);
        loadBalance(blbTokenContract, hiLoCardsContract);
        loadActiveSession(hiLoCardsContract);
        checkAdminAccess(hiLoCardsContract);
      }, 500);

    } catch (error) {
      console.log('Error initializing contracts:', error);
      setContracts({ hiLoCards: null, blbToken: null });
    }
  };

  // Check admin access
  const checkAdminAccess = async (contract = contracts.hiLoCards) => {
    if (!contract || !walletData?.account) return;

    try {
      const owner = await contract.owner();
      const userIsOwner = owner.toLowerCase() === walletData.account.toLowerCase();
      
      const admin = await contract.admin();
      const userIsAdmin = admin.toLowerCase() === walletData.account.toLowerCase();

      setIsOwner(userIsOwner);
      setIsAdmin(userIsAdmin);

      if (userIsOwner || userIsAdmin) {
        await loadAdminData(contract);
      }
    } catch (error) {
      console.error('Error checking admin access:', error);
      setIsOwner(false);
      setIsAdmin(false);
    }
  };

  // Load admin data
  const loadAdminData = async (contract = contracts.hiLoCards) => {
    if (!contract) return;

    try {
      const [houseBalance, isPaused, betLimits] = await Promise.all([
        contract.houseBalance(),
        contract.paused(),
        Promise.all([contract.minBet(), contract.maxBet()])
      ]);

      setAdminState(prev => ({
        ...prev,
        houseBalance: ethers.utils.formatEther(houseBalance),
        isPaused,
        minBet: ethers.utils.formatEther(betLimits[0]),
        maxBet: ethers.utils.formatEther(betLimits[1])
      }));
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  // Load active session
  const loadActiveSession = async (contract = contracts.hiLoCards) => {
    if (!contract || !walletData?.account) return;

    try {
      const session = await contract.getActiveSession(walletData.account);
      
      if (session.active) {
        setGameState(prev => ({
          ...prev,
          session: {
            player: session.player,
            initialBet: ethers.utils.formatEther(session.initialBet),
            currentWinnings: ethers.utils.formatEther(session.currentWinnings),
            currentCard: parseInt(session.currentCard),
            streak: parseInt(session.streak),
            active: session.active,
            startTime: parseInt(session.startTime)
          },
          isPlaying: true,
          reactorPower: parseInt(session.streak) * 20
        }));
      } else {
        setGameState(prev => ({
          ...prev,
          session: null,
          isPlaying: false,
          reactorPower: 0
        }));
      }
    } catch (error) {
      console.log('No active session or error loading session:', error);
      setGameState(prev => ({
        ...prev,
        session: null,
        isPlaying: false,
        reactorPower: 0
      }));
    }
  };

  // Load game data from contract
  const loadGameData = async (contract = contracts.hiLoCards) => {
    if (!contract) return;

    try {
      const [stats, recentGames, config] = await Promise.all([
        contract.getGameStats(),
        contract.getRecentGames(5).catch(() => []),
        contract.getGameConfig()
      ]);

      setGameStats({
        totalGames: parseInt(stats.totalGames.toString()),
        totalBets: ethers.utils.formatEther(stats.totalBets),
        totalPayouts: ethers.utils.formatEther(stats.totalPayoutsAmount),
        houseEdge: '5', // Calculate from config if needed
        effectiveMultiplier: (parseInt(config.correctPayout.toString()) / 100).toString(),
        playersOnline: Math.floor(Math.random() * 200) + 30
      });

      if (recentGames && recentGames.length > 0) {
        const formattedHistory = recentGames.slice(0, 5).map(game => ({
          initialCard: parseInt(game.initialCard),
          finalCard: parseInt(game.finalCard),
          prediction: parseInt(game.prediction),
          streak: parseInt(game.streak),
          won: game.won,
          cashedOut: game.cashedOut,
          betAmount: ethers.utils.formatEther(game.betAmount),
          netPayout: ethers.utils.formatEther(game.netPayout),
          timestamp: parseInt(game.timestamp) * 1000
        }));
        setGameHistory(formattedHistory);
      }
    } catch (error) {
      console.error('Error loading game data:', error);
    }
  };

  // Load BLB balance
  const loadBalance = async (tokenContract = contracts.blbToken, gameContract = contracts.hiLoCards) => {
    if (!tokenContract || !gameContract || !walletData?.account) return;

    try {
      const [balance, allowance] = await Promise.all([
        tokenContract.balanceOf(walletData.account),
        tokenContract.allowance(walletData.account, gameContract.address)
      ]);

      setGameState(prev => ({
        ...prev,
        balance: ethers.utils.formatEther(balance),
        allowance: ethers.utils.formatEther(allowance)
      }));
    } catch (error) {
      console.error('Error loading balance:', error);
    }
  };

  // Handle game start
  const handleInitiateFusion = async () => {
    if (!contracts.hiLoCards || !contracts.blbToken) {
      setErrorMessage('Quantum systems offline. Reconnect and try again.');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');

      const betAmount = parseFloat(gameState.betAmount);
      const balance = parseFloat(gameState.balance);

      if (betAmount <= 0) {
        setErrorMessage('Energy input must be greater than 0');
        return;
      }

      if (betAmount > balance) {
        setErrorMessage('Insufficient energy reserves');
        return;
      }

      if (gameState.session?.active) {
        setErrorMessage('Reactor already active');
        return;
      }

      playGameSound('reactorHum', 0.4);
      
      setGameState(prev => ({ 
        ...prev, 
        isPlaying: true,
        showFusionPopup: true
      }));
      setAnimationState('initiating');

      const betAmountWei = ethers.utils.parseEther(gameState.betAmount);
      const tx = await contracts.hiLoCards.startGame(betAmountWei);
      
      setSuccessMessage('Fusion initiated! Reactor charging...');
      
      const receipt = await tx.wait();
      
      setTimeout(() => {
        playGameSound('energySurge', 0.4);
      }, 2000);
      
      const gameStartedEvent = receipt.events?.find(event => event.event === 'GameStarted');
      
      if (gameStartedEvent) {
        const [player, betAmount, initialCard] = gameStartedEvent.args;
        
        setTimeout(() => {
          handleGameStarted({
            player,
            betAmount: ethers.utils.formatEther(betAmount),
            initialCard: parseInt(initialCard)
          });
        }, 2500);
        
        await Promise.all([loadGameData(), loadBalance(), loadActiveSession()]);
      } else {
        throw new Error('Game start event not found in transaction receipt');
      }

    } catch (error) {
      console.error('Fusion initiation error:', error);
      
      if (error.code === 4001) {
        setErrorMessage('Fusion cancelled by operator');
      } else if (error.code === -32603) {
        setErrorMessage('Fusion failed. Check your balance and allowance.');
      } else if (error.message.includes('insufficient funds')) {
        setErrorMessage('Insufficient gas or energy balance');
      } else if (error.message.includes('allowance')) {
        setErrorMessage('Please authorize energy transfer first');
      } else if (error.message.includes('Game already active')) {
        setErrorMessage('Reactor already active');
      } else {
        setErrorMessage(`Fusion failed: ${error.reason || error.message}`);
      }
      
      setAnimationState('idle');
      setGameState(prev => ({ 
        ...prev, 
        isPlaying: false,
        showFusionPopup: false
      }));
      setSuccessMessage('');
    }
  };

  const handleGameStarted = (gameData) => {
    setGameState(prev => ({
      ...prev,
      isPlaying: true,
      session: {
        player: gameData.player,
        initialBet: gameData.betAmount,
        currentWinnings: gameData.betAmount,
        currentCard: gameData.initialCard,
        streak: 0,
        active: true,
        startTime: Date.now()
      },
      showFusionPopup: false,
      reactorPower: 10
    }));

    setAnimationState('idle');
    setSuccessMessage(`⚡ Reactor online! Initial energy cell: ${CARD_NAMES[gameData.initialCard - 1]}`);
    
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Handle predictions
  const handleMakePrediction = async (predictionType) => {
    if (!contracts.hiLoCards || !gameState.session?.active) {
      setErrorMessage('No active reactor session');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');
      playGameSound(predictionType === 0 ? 'energyDecay' : 'energySurge', 0.4);
      
      setAnimationState('predicting');
      
      // Show prediction result popup immediately
      setGameState(prev => ({ ...prev, showPredictionResult: true }));
      
      const tx = await contracts.hiLoCards.makePrediction(predictionType);
      
      setSuccessMessage(`${predictionType === 0 ? 'Energy decay' : 'Energy surge'} initiated...`);
      
      const receipt = await tx.wait();
      
      const predictionEvent = receipt.events?.find(event => event.event === 'PredictionMade');
      const gameEndedEvent = receipt.events?.find(event => event.event === 'GameEnded');
      
      if (predictionEvent) {
        const [player, prediction, newCard, correct, streak] = predictionEvent.args;
        
        setTimeout(() => {
          handlePredictionResult({
            player,
            prediction: parseInt(prediction),
            newCard: parseInt(newCard),
            correct,
            streak: parseInt(streak)
          }, gameEndedEvent);
        }, 2000);
        
        await Promise.all([loadGameData(), loadBalance(), loadActiveSession()]);
      } else if (gameEndedEvent) {
        // Game ended immediately (house won)
        setTimeout(() => {
          handleGameEnded(false);
        }, 2000);
        
        await Promise.all([loadGameData(), loadBalance(), loadActiveSession()]);
      } else {
        throw new Error('Game event not found in transaction receipt');
      }

    } catch (error) {
      console.error('Prediction error:', error);
      
      if (error.code === 4001) {
        setErrorMessage('Prediction cancelled by operator');
      } else if (error.message.includes('No active game')) {
        setErrorMessage('No active reactor session');
      } else if (error.message.includes('Max streak reached')) {
        setErrorMessage('Maximum energy level reached!');
      } else {
        setErrorMessage(`Prediction failed: ${error.reason || error.message}`);
      }
      
      setAnimationState('idle');
      setGameState(prev => ({ ...prev, showPredictionResult: false }));
      setSuccessMessage('');
    }
  };

  const handlePredictionResult = (predictionData, gameEndedEvent) => {
    setPredictionResult(predictionData);
    setAnimationState('result');

    setTimeout(() => {
      if (predictionData.correct && !gameEndedEvent) {
        playGameSound('fusionSuccess', 0.6);
        setSuccessMessage(`⚡ Energy amplified! Streak: ${predictionData.streak}`);
        
        setGameState(prev => ({
          ...prev,
          reactorPower: Math.min(predictionData.streak * 20, 100),
          totalWins: prev.totalWins + 1,
          experience: prev.experience + 100
        }));
      } else {
        playGameSound('coreMeltdown', 0.4);
        setErrorMessage(`🔥 Core meltdown! Energy dispersed`);
        
        setGameState(prev => ({
          ...prev,
          session: null,
          isPlaying: false,
          reactorPower: 0
        }));
      }
    }, 500);

    setTimeout(() => {
      setAnimationState('idle');
      setPredictionResult(null);
      setGameState(prev => ({ ...prev, showPredictionResult: false }));
      setErrorMessage('');
      setSuccessMessage('');
    }, 4000);
  };

  const handleGameEnded = (won) => {
    if (won) {
      playGameSound('fusionSuccess', 0.6);
      setSuccessMessage('⚡ Maximum energy harvested!');
    } else {
      playGameSound('coreMeltdown', 0.4);
      setErrorMessage('🔥 Reactor overloaded!');
    }

    setGameState(prev => ({
      ...prev,
      session: null,
      isPlaying: false,
      reactorPower: 0,
      showPredictionResult: false
    }));

    setTimeout(() => {
      setErrorMessage('');
      setSuccessMessage('');
    }, 4000);
  };

  // Handle cash out
  const handleHarvestEnergy = async () => {
    if (!contracts.hiLoCards || !gameState.session?.active) {
      setErrorMessage('No active reactor session');
      return;
    }

    if (gameState.session.streak === 0) {
      setErrorMessage('Need at least one successful fusion to harvest');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');
      playGameSound('reactorOverload', 0.4);
      
      setAnimationState('harvesting');
      
      const tx = await contracts.hiLoCards.cashOut();
      
      setSuccessMessage('Harvesting energy...');
      
      const receipt = await tx.wait();
      
      const cashedOutEvent = receipt.events?.find(event => event.event === 'GameCashedOut');
      
      if (cashedOutEvent) {
        const [player, grossPayout, netPayout] = cashedOutEvent.args;
        
        setTimeout(() => {
          handleEnergyHarvested({
            grossPayout: ethers.utils.formatEther(grossPayout),
            netPayout: ethers.utils.formatEther(netPayout)
          });
        }, 2000);
        
        await Promise.all([loadGameData(), loadBalance(), loadActiveSession()]);
      } else {
        throw new Error('Cash out event not found in transaction receipt');
      }

    } catch (error) {
      console.error('Energy harvest error:', error);
      
      if (error.code === 4001) {
        setErrorMessage('Harvest cancelled by operator');
      } else if (error.message.includes('No active game')) {
        setErrorMessage('No active reactor session');
      } else if (error.message.includes('Must have at least one win')) {
        setErrorMessage('Need successful fusion first');
      } else {
        setErrorMessage(`Harvest failed: ${error.reason || error.message}`);
      }
      
      setAnimationState('idle');
      setSuccessMessage('');
    }
  };

  const handleEnergyHarvested = (harvestData) => {
    playGameSound('fusionSuccess', 0.8);
    setSuccessMessage(`⚡ Energy harvested: +${formatNumber(harvestData.netPayout)} BLB`);
    
    setGameState(prev => ({
      ...prev,
      session: null,
      isPlaying: false,
      reactorPower: 0,
      experience: prev.experience + 200,
      totalWins: prev.totalWins + 1
    }));

    setTimeout(() => {
      setSuccessMessage('');
    }, 4000);
  };

  // Handle token approval
  const handleApprove = async () => {
    if (!contracts.blbToken || !contracts.hiLoCards) {
      setErrorMessage('Quantum systems offline. Reconnect and try again.');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');
      playGameSound('quantumClick', 0.3);
      setGameState(prev => ({ ...prev, isApproving: true }));

      const maxAmount = ethers.constants.MaxUint256;
      const tx = await contracts.blbToken.approve(contracts.hiLoCards.address, maxAmount);
      
      setSuccessMessage('Energy transfer authorization initiated...');
      await tx.wait();
      
      await loadBalance();
      setSuccessMessage('✅ Energy systems authorized!');
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Approval error:', error);
      
      if (error.code === 4001) {
        setErrorMessage('Authorization denied by operator');
      } else {
        setErrorMessage(`Authorization failed: ${error.reason || error.message}`);
      }
    } finally {
      setGameState(prev => ({ ...prev, isApproving: false }));
    }
  };

  const needsApproval = () => {
    return parseFloat(gameState.allowance) < parseFloat(gameState.betAmount);
  };

  const canInitiateFusion = () => {
    return connected && 
           contracts.hiLoCards &&
           contracts.blbToken &&
           !gameState.session?.active &&
           parseFloat(gameState.balance) >= parseFloat(gameState.betAmount) &&
           parseFloat(gameState.betAmount) > 0 &&
           !needsApproval() &&
           !gameState.isPlaying;
  };

  const formatNumber = (num) => {
    const n = parseFloat(num);
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toFixed(2);
  };

  const getCardSymbol = (cardNumber) => {
    if (!cardNumber || cardNumber < 1 || cardNumber > 13) return '⚡';
    return CARD_SYMBOLS[cardNumber - 1];
  };

  const getReactorStatus = () => {
    if (!gameState.session?.active) return 'OFFLINE';
    if (gameState.session.streak === 0) return 'STANDBY';
    if (gameState.session.streak < 3) return 'ACTIVE';
    if (gameState.session.streak < 5) return 'CRITICAL';
    return 'OVERLOAD';
  };

  // Connection state
  useEffect(() => {
    if (walletData?.account) {
      setConnected(true);
    }
  }, [walletData]);

  if (!connected) {
    return (
      <div className="quantum-reactor-arena">
        <div className="energy-field">
          {energyParticles.map(particle => (
            <div
              key={particle.id}
              className="energy-particle"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                opacity: particle.opacity,
                animationDelay: `${particle.id * 0.1}s`
              }}
            >
              ⚡
            </div>
          ))}
        </div>
        <div className="quantum-connect-screen">
          <div className="connect-icon">⚛️</div>
          <h3 className="connect-title">⚡ REACTOR OFFLINE</h3>
          <p className="connect-desc">Connect your neural interface to access the Quantum Energy Reactor</p>
        </div>
      </div>
    );
  }

  if (!contracts.hiLoCards || !contracts.blbToken) {
    return (
      <div className="quantum-reactor-arena">
        <div className="energy-field">
          {energyParticles.map(particle => (
            <div
              key={particle.id}
              className="energy-particle"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                opacity: particle.opacity,
                animationDelay: `${particle.id * 0.1}s`
              }}
            >
              ⚡
            </div>
          ))}
        </div>
        <div className="quantum-connect-screen">
          <div className="connect-icon">🔄</div>
          <h3 className="connect-title">🔗 INITIALIZING REACTOR</h3>
          <p className="connect-desc">Establishing quantum field connections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="quantum-reactor-arena">
      {/* Energy Field Background */}
      <div className="energy-field">
        {energyParticles.map(particle => (
          <div
            key={particle.id}
            className="energy-particle"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              opacity: particle.opacity,
              backgroundColor: particle.color,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              animationDelay: `${particle.id * 0.1}s`
            }}
          />
        ))}
      </div>

      {/* Header */}
      <div className="quantum-header-bar">
        <button 
          className="quantum-back-btn" 
          onClick={() => {
            playGameSound('quantumClick', 0.3);
            if (onBack) onBack();
          }}
          onMouseEnter={() => playGameSound('quantumHover', 0.2)}
        >
          ← SHUTDOWN
        </button>
        <div className="quantum-title-section">
          <h2 className="quantum-main-title">⚡ QUANTUM REACTOR</h2>
          <div className="quantum-subtitle">Energy Amplification Protocol v{gameStats.effectiveMultiplier}</div>
        </div>
        <div className="quantum-balance-display">
          🔋 {formatNumber(gameState.balance)} BLB
          <div className="quantum-player-level">
            LVL {Math.floor(gameState.experience / 500) + 1}
          </div>
          <button
            onClick={() => {
              setSoundEnabled(!soundEnabled);
              playGameSound('quantumClick', 0.2);
            }}
            className="quantum-sound-btn"
            title={soundEnabled ? 'Disable Audio' : 'Enable Audio'}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
          {(isOwner || isAdmin) && (
            <button
              onClick={() => {
                setShowAdminPanel(!showAdminPanel);
                playGameSound('quantumClick', 0.3);
              }}
              onMouseEnter={() => playGameSound('quantumHover', 0.2)}
              className={`quantum-admin-toggle-btn ${showAdminPanel ? 'active' : ''}`}
              title="Admin Terminal"
            >
              ⚙️
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      {errorMessage && (
        <div className="quantum-message quantum-error-msg">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="quantum-message quantum-success-msg">
          {successMessage}
        </div>
      )}

      <div className="quantum-game-layout">
        {/* Admin Panel */}
        {showAdminPanel && (isOwner || isAdmin) && (
          <div className="quantum-admin-master-panel">
            <div className="quantum-admin-panel-header">
              <h3>⚛️ REACTOR CONTROL CENTER</h3>
              <div className="quantum-admin-role-badge">
                {isOwner ? '👑 CHIEF ENGINEER' : '⚡ REACTOR ENGINEER'}
              </div>
              <button
                onClick={() => setShowAdminPanel(false)}
                className="quantum-admin-close-btn"
                style={{
                  background: 'rgba(255, 69, 0, 0.3)',
                  border: '2px solid #FF4500',
                  borderRadius: '8px',
                  color: '#FFB347',
                  padding: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem'
                }}
              >
                ❌
              </button>
            </div>

            <div className="quantum-admin-panel-content" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginTop: '2rem' }}>
              {/* Reactor Control Status */}
              <div className="quantum-admin-section" style={{
                background: 'linear-gradient(135deg, rgba(65, 105, 225, 0.2) 0%, rgba(0, 206, 209, 0.1) 100%)',
                border: '3px solid rgba(65, 105, 225, 0.5)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="quantum-admin-section-title" style={{ color: '#4169E1', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  ⚡ Reactor Control Status
                </h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <span style={{ color: 'rgba(65, 105, 225, 0.8)', fontWeight: '600' }}>Status:</span>
                  <span style={{
                    color: adminState.isPaused ? '#FF4500' : '#00CED1',
                    fontWeight: '800',
                    textShadow: '0 0 10px currentColor'
                  }}>
                    {adminState.isPaused ? '⏸️ SHUTDOWN' : '▶️ OPERATIONAL'}
                  </span>
                </div>
                <button
                  onClick={async () => {
                    try {
                      setErrorMessage('');
                      playGameSound('quantumClick', 0.3);
                      
                      if (contracts.hiLoCards) {
                        const tx = adminState.isPaused ? 
                          await contracts.hiLoCards.unpauseGame() : 
                          await contracts.hiLoCards.pauseGame();
                        
                        setSuccessMessage(`Reactor ${adminState.isPaused ? 'activation' : 'shutdown'} initiated...`);
                        await tx.wait();
                        
                        await loadAdminData();
                        setSuccessMessage(`✅ Reactor ${adminState.isPaused ? 'activated' : 'shutdown'} successfully!`);
                      } else {
                        setSuccessMessage(`✅ Reactor ${adminState.isPaused ? 'activated' : 'shutdown'}! (Demo)`);
                      }
                      setTimeout(() => setSuccessMessage(''), 3000);
                    } catch (error) {
                      console.error('Error toggling reactor:', error);
                      setErrorMessage(`Failed to ${adminState.isPaused ? 'activate' : 'shutdown'} reactor: ${error.reason || error.message}`);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    background: adminState.isPaused ? 
                      'linear-gradient(135deg, #00CED1 0%, #4169E1 100%)' : 
                      'linear-gradient(135deg, #FF4500 0%, #DC143C 100%)',
                    border: `3px solid ${adminState.isPaused ? '#00CED1' : '#FF4500'}`,
                    borderRadius: '10px',
                    color: '#FFF',
                    cursor: 'pointer',
                    fontWeight: '800',
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    boxShadow: `0 0 15px rgba(${adminState.isPaused ? '0, 206, 209' : '255, 69, 0'}, 0.4)`
                  }}
                >
                  {adminState.isPaused ? '▶️ ACTIVATE' : '⏸️ SHUTDOWN'}
                </button>
              </div>

              {/* Energy Treasury */}
              <div className="quantum-admin-section" style={{
                background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(255, 165, 0, 0.1) 100%)',
                border: '3px solid rgba(255, 215, 0, 0.4)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="quantum-admin-section-title" style={{ color: '#FFD700', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  🔋 Energy Treasury
                </h4>
                <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                  <div style={{ color: 'rgba(255, 215, 0, 0.8)', fontWeight: '600', marginBottom: '0.5rem' }}>
                    Current Reserves:
                  </div>
                  <div style={{ 
                    color: '#FFD700', 
                    fontSize: '1.5rem', 
                    fontWeight: '800',
                    textShadow: '0 0 15px currentColor'
                  }}>
                    ⚡ {formatNumber(adminState.houseBalance)} BLB
                  </div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      setErrorMessage('');
                      playGameSound('quantumClick', 0.3);
                      
                      if (contracts.hiLoCards && contracts.hiLoCards.emergencyWithdrawAll) {
                        const tx = await contracts.hiLoCards.emergencyWithdrawAll();
                        setSuccessMessage('Extracting all energy reserves...');
                        await tx.wait();
                        
                        await Promise.all([loadAdminData(), loadBalance()]);
                        setSuccessMessage('✅ Emergency energy extraction completed!');
                      } else {
                        setSuccessMessage('✅ Emergency energy extraction completed! (Demo)');
                      }
                      setTimeout(() => setSuccessMessage(''), 3000);
                    } catch (error) {
                      console.error('Emergency withdraw error:', error);
                      setErrorMessage(`Emergency extraction failed: ${error.reason || error.message}`);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    background: 'linear-gradient(135deg, #FF4500 0%, #DC143C 100%)',
                    border: '3px solid #FF4500',
                    borderRadius: '10px',
                    color: '#FFF',
                    cursor: 'pointer',
                    fontWeight: '800',
                    fontSize: '0.9rem',
                    textTransform: 'uppercase',
                    boxShadow: '0 0 15px rgba(255, 69, 0, 0.4)'
                  }}
                >
                  🚨 EMERGENCY EXTRACTION
                </button>
              </div>

              {/* Fund Treasury */}
              <div className="quantum-admin-section" style={{
                background: 'linear-gradient(135deg, rgba(0, 206, 209, 0.15) 0%, rgba(65, 105, 225, 0.1) 100%)',
                border: '3px solid rgba(0, 206, 209, 0.4)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="quantum-admin-section-title" style={{ color: '#00CED1', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  💎 Fund Treasury
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input
                    type="number"
                    value={adminState.fundAmount}
                    onChange={(e) => setAdminState(prev => ({ ...prev, fundAmount: e.target.value }))}
                    placeholder="Amount to fund energy treasury"
                    min="0"
                    step="1"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'rgba(0, 0, 0, 0.7)',
                      border: '2px solid rgba(0, 206, 209, 0.5)',
                      borderRadius: '10px',
                      color: '#00CED1',
                      fontSize: '1rem',
                      fontWeight: '700',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const amount = adminState.fundAmount;
                        if (!amount || parseFloat(amount) <= 0) {
                          setErrorMessage('Please enter a valid fund amount');
                          return;
                        }
                        
                        playGameSound('quantumClick', 0.3);
                        
                        if (contracts.hiLoCards && contracts.blbToken) {
                          const amountWei = ethers.utils.parseEther(amount);
                          
                          const allowance = await contracts.blbToken.allowance(walletData.account, contracts.hiLoCards.address);
                          if (allowance.lt(amountWei)) {
                            const approveTx = await contracts.blbToken.approve(contracts.hiLoCards.address, ethers.constants.MaxUint256);
                            await approveTx.wait();
                          }
                          
                          const tx = await contracts.hiLoCards.fundHouse(amountWei);
                          setSuccessMessage('Funding treasury...');
                          await tx.wait();
                          
                          await Promise.all([loadAdminData(), loadBalance()]);
                          setSuccessMessage(`✅ Treasury funded with ${formatNumber(amount)} BLB!`);
                        } else {
                          setSuccessMessage(`✅ Treasury funded with ${formatNumber(amount)} BLB! (Demo)`);
                        }
                        
                        setAdminState(prev => ({ ...prev, fundAmount: '' }));
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error funding treasury:', error);
                        setErrorMessage(`Failed to fund treasury: ${error.reason || error.message}`);
                      }
                    }}
                    disabled={!adminState.fundAmount}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #00CED1 0%, #4169E1 100%)',
                      border: '3px solid #00CED1',
                      borderRadius: '10px',
                      color: '#FFF',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      opacity: adminState.fundAmount ? 1 : 0.6,
                      boxShadow: adminState.fundAmount ? '0 0 15px rgba(0, 206, 209, 0.4)' : 'none'
                    }}
                  >
                    💎 FUND TREASURY
                  </button>
                </div>
              </div>

              {/* Withdraw Treasury */}
              <div className="quantum-admin-section" style={{
                background: 'linear-gradient(135deg, rgba(255, 165, 0, 0.15) 0%, rgba(255, 69, 0, 0.1) 100%)',
                border: '3px solid rgba(255, 165, 0, 0.4)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="quantum-admin-section-title" style={{ color: '#FFA500', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  🔥 Withdraw Treasury
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input
                    type="number"
                    value={adminState.withdrawAmount}
                    onChange={(e) => setAdminState(prev => ({ ...prev, withdrawAmount: e.target.value }))}
                    placeholder="Amount to withdraw"
                    min="0"
                    step="1"
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'rgba(0, 0, 0, 0.7)',
                      border: '2px solid rgba(255, 165, 0, 0.5)',
                      borderRadius: '10px',
                      color: '#FFA500',
                      fontSize: '1rem',
                      fontWeight: '700',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const amount = adminState.withdrawAmount;
                        if (!amount || parseFloat(amount) <= 0) {
                          setErrorMessage('Please enter a valid withdraw amount');
                          return;
                        }
                        
                        playGameSound('quantumClick', 0.3);
                        
                        if (contracts.hiLoCards) {
                          const amountWei = ethers.utils.parseEther(amount);
                          const tx = await contracts.hiLoCards.withdrawHouse(amountWei);
                          setSuccessMessage('Withdrawing from treasury...');
                          await tx.wait();
                          
                          await Promise.all([loadAdminData(), loadBalance()]);
                          setSuccessMessage(`✅ Withdrew ${formatNumber(amount)} BLB from treasury!`);
                        } else {
                          setSuccessMessage(`✅ Withdrew ${formatNumber(amount)} BLB from treasury! (Demo)`);
                        }
                        
                        setAdminState(prev => ({ ...prev, withdrawAmount: '' }));
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error withdrawing from treasury:', error);
                        setErrorMessage(`Failed to withdraw: ${error.reason || error.message}`);
                      }
                    }}
                    disabled={!adminState.withdrawAmount}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #FFA500 0%, #FF4500 100%)',
                      border: '3px solid #FFA500',
                      borderRadius: '10px',
                      color: '#FFF',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      opacity: adminState.withdrawAmount ? 1 : 0.6,
                      boxShadow: adminState.withdrawAmount ? '0 0 15px rgba(255, 165, 0, 0.4)' : 'none'
                    }}
                  >
                    🔥 WITHDRAW
                  </button>
                </div>
              </div>

              {/* Energy Limits */}
              <div className="quantum-admin-section" style={{
                background: 'linear-gradient(135deg, rgba(138, 43, 226, 0.15) 0%, rgba(75, 0, 130, 0.1) 100%)',
                border: '3px solid rgba(138, 43, 226, 0.4)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="quantum-admin-section-title" style={{ color: '#8A2BE2', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  ⚡ Energy Limits
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    <div>
                      <label style={{ color: 'rgba(138, 43, 226, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        MIN ENERGY
                      </label>
                      <input
                        type="number"
                        value={adminState.newMinBet || adminState.minBet}
                        onChange={(e) => setAdminState(prev => ({ ...prev, newMinBet: e.target.value }))}
                        min="1"
                        step="1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(138, 43, 226, 0.5)',
                          borderRadius: '8px',
                          color: '#8A2BE2',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(138, 43, 226, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        MAX ENERGY
                      </label>
                      <input
                        type="number"
                        value={adminState.newMaxBet || adminState.maxBet}
                        onChange={(e) => setAdminState(prev => ({ ...prev, newMaxBet: e.target.value }))}
                        min="1"
                        step="1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(138, 43, 226, 0.5)',
                          borderRadius: '8px',
                          color: '#8A2BE2',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const minBet = parseFloat(adminState.newMinBet || adminState.minBet);
                        const maxBet = parseFloat(adminState.newMaxBet || adminState.maxBet);

                        if (minBet <= 0 || maxBet <= 0 || minBet >= maxBet) {
                          setErrorMessage('Invalid energy limits');
                          return;
                        }

                        playGameSound('quantumClick', 0.3);
                        
                        if (contracts.hiLoCards) {
                          const minBetWei = ethers.utils.parseEther(minBet.toString());
                          const maxBetWei = ethers.utils.parseEther(maxBet.toString());
                          
                          const tx = await contracts.hiLoCards.setBetLimits(minBetWei, maxBetWei);
                          setSuccessMessage('Updating energy limits...');
                          await tx.wait();
                          
                          await loadAdminData();
                          setSuccessMessage(`✅ Limits updated! Min: ${minBet} BLB, Max: ${maxBet} BLB`);
                        } else {
                          setSuccessMessage(`✅ Limits updated! Min: ${minBet} BLB, Max: ${maxBet} BLB (Demo)`);
                        }
                        
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error setting energy limits:', error);
                        setErrorMessage(`Failed to set limits: ${error.reason || error.message}`);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #8A2BE2 0%, #4B0082 100%)',
                      border: '3px solid #8A2BE2',
                      borderRadius: '10px',
                      color: '#FFF',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      boxShadow: '0 0 15px rgba(138, 43, 226, 0.4)'
                    }}
                  >
                    ⚡ SET LIMITS
                  </button>
                </div>
              </div>

              {/* Reactor Configuration */}
              <div className="quantum-admin-section" style={{
                background: 'linear-gradient(135deg, rgba(25, 25, 112, 0.2) 0%, rgba(72, 61, 139, 0.1) 100%)',
                border: '3px solid rgba(25, 25, 112, 0.5)',
                borderRadius: '15px',
                padding: '1.5rem'
              }}>
                <h4 className="quantum-admin-section-title" style={{ color: '#ffffffff', marginBottom: '1rem', textTransform: 'uppercase', fontSize: '1rem' }}>
                  ⚛️ Reactor Configuration
                </h4>
                <div style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)', marginBottom: '1rem', fontStyle: 'italic', textAlign: 'center' }}>
                  Configure energy prediction success rates and fusion payouts
                </div>
                
                {/* Win Chances Configuration */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ color: '#ffffffff', fontWeight: '700', marginBottom: '0.8rem', textAlign: 'center' }}>
                    PREDICTION SUCCESS RATES (%)
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    <div>
                      <label style={{ color: 'rgba(252, 252, 252, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        🔴 NO WIN CHANCE (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newWinChances?.noWin || '50.0'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newWinChances: { 
                            ...prev.newWinChances, 
                            noWin: e.target.value 
                          }
                        }))}
                        min="0"
                        max="95"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(25, 25, 112, 0.5)',
                          borderRadius: '8px',
                          color: '#191970',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        ✅ CORRECT PREDICTION CHANCE (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newWinChances?.correct || '20.0'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newWinChances: { 
                            ...prev.newWinChances, 
                            correct: e.target.value 
                          }
                        }))}
                        min="0"
                        max="95"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(25, 25, 112, 0.5)',
                          borderRadius: '8px',
                          color: '#191970',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Payout Configuration */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <div style={{ color: '#ffffffff', fontWeight: '700', marginBottom: '0.8rem', textAlign: 'center' }}>
                    FUSION PAYOUTS
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    <div>
                      <label style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        ⚡ CORRECT PAYOUT (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newPayouts?.correctPayout || '190'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newPayouts: { 
                            ...prev.newPayouts, 
                            correctPayout: e.target.value 
                          }
                        }))}
                        min="100"
                        max="500"
                        step="1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(25, 25, 112, 0.5)',
                          borderRadius: '8px',
                          color: '#191970',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(253, 253, 255, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        🔥 MAX STREAK
                      </label>
                      <input
                        type="number"
                        value={adminState.newPayouts?.maxStreak || '5'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newPayouts: { 
                            ...prev.newPayouts, 
                            maxStreak: e.target.value 
                          }
                        }))}
                        min="1"
                        max="20"
                        step="1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(25, 25, 112, 0.5)',
                          borderRadius: '8px',
                          color: '#191970',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                        ⚛️ STREAK MULTIPLIER (%)
                      </label>
                      <input
                        type="number"
                        value={adminState.newPayouts?.streakMultiplier || '150'}
                        onChange={(e) => setAdminState(prev => ({ 
                          ...prev, 
                          newPayouts: { 
                            ...prev.newPayouts, 
                            streakMultiplier: e.target.value 
                          }
                        }))}
                        min="100"
                        max="300"
                        step="1"
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: 'rgba(0, 0, 0, 0.7)',
                          border: '2px solid rgba(25, 25, 112, 0.5)',
                          borderRadius: '8px',
                          color: '#191970',
                          fontSize: '0.9rem',
                          fontWeight: '700',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Update Buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                  <button
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const noWin = parseFloat(adminState.newWinChances?.noWin || '50.0');
                        const correct = parseFloat(adminState.newWinChances?.correct || '20.0');

                        if (noWin < 0 || correct < 0 || noWin + correct > 100) {
                          setErrorMessage('Invalid win chances - total cannot exceed 100%');
                          return;
                        }

                        playGameSound('quantumClick', 0.3);
                        
                        if (contracts.hiLoCards) {
                          const noWinBasisPoints = Math.floor(noWin * 100);
                          const correctBasisPoints = Math.floor(correct * 100);
                          
                          const tx = await contracts.hiLoCards.updateWinChances(noWinBasisPoints, correctBasisPoints);
                          setSuccessMessage('Updating prediction parameters...');
                          await tx.wait();
                          
                          await loadAdminData();
                          setSuccessMessage(`✅ Win chances updated! No Win: ${noWin}%, Correct: ${correct}%`);
                        } else {
                          setSuccessMessage(`✅ Win chances updated! No Win: ${noWin}%, Correct: ${correct}% (Demo)`);
                        }
                        
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error updating win chances:', error);
                        setErrorMessage(`Failed to update win chances: ${error.reason || error.message}`);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #191970 0%, #4169E1 100%)',
                      border: '3px solid #191970',
                      borderRadius: '10px',
                      color: '#FFF',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      boxShadow: '0 0 15px rgba(25, 25, 112, 0.4)'
                    }}
                  >
                    🎯 UPDATE WIN CHANCES
                  </button>
                  
                  <button
                    onClick={async () => {
                      try {
                        setErrorMessage('');
                        const correctPayout = parseFloat(adminState.newPayouts?.correctPayout || '190');
                        const maxStreak = parseFloat(adminState.newPayouts?.maxStreak || '5');
                        const streakMultiplier = parseFloat(adminState.newPayouts?.streakMultiplier || '150');

                        if (correctPayout < 100 || maxStreak < 1 || streakMultiplier < 100) {
                          setErrorMessage('Invalid payout parameters');
                          return;
                        }

                        playGameSound('quantumClick', 0.3);
                        
                        if (contracts.hiLoCards) {
                          const correctPayoutBasisPoints = Math.floor(correctPayout);
                          const streakMultiplierBasisPoints = Math.floor(streakMultiplier);
                          
                          const tx = await contracts.hiLoCards.updatePayouts(correctPayoutBasisPoints, maxStreak, streakMultiplierBasisPoints);
                          setSuccessMessage('Updating fusion parameters...');
                          await tx.wait();
                          
                          await loadAdminData();
                          setSuccessMessage(`✅ Payouts updated! Correct: ${correctPayout}%, Max Streak: ${maxStreak}, Multiplier: ${streakMultiplier}%`);
                        } else {
                          setSuccessMessage(`✅ Payouts updated! Correct: ${correctPayout}%, Max Streak: ${maxStreak}, Multiplier: ${streakMultiplier}% (Demo)`);
                        }
                        
                        setTimeout(() => setSuccessMessage(''), 3000);
                      } catch (error) {
                        console.error('Error updating payouts:', error);
                        setErrorMessage(`Failed to update payouts: ${error.reason || error.message}`);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      background: 'linear-gradient(135deg, #8A2BE2 0%, #4B0082 100%)',
                      border: '3px solid #8A2BE2',
                      borderRadius: '10px',
                      color: '#FFF',
                      cursor: 'pointer',
                      fontWeight: '800',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      boxShadow: '0 0 15px rgba(138, 43, 226, 0.4)'
                    }}
                  >
                    ⚛️ UPDATE PAYOUTS
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Game Zone */}
        <div className="quantum-game-zone">
          {/* Fusion Popup */}
          {gameState.showFusionPopup && (
            <div className="quantum-fusion-popup-overlay">
              <div className="quantum-fusion-popup">
                <div className="fusion-header">
                  <h2>⚡ INITIATING QUANTUM FUSION!</h2>
                  <div className="fusion-info">
                    <span>Energy Input: {formatNumber(gameState.betAmount)} BLB</span>
                  </div>
                </div>

                <div className="fusion-display-zone">
                  <div className="fusion-reactor">
                    <div className="reactor-core pulsing">⚛️</div>
                    <div className="energy-rings">
                      <div className="energy-ring ring-1"></div>
                      <div className="energy-ring ring-2"></div>
                      <div className="energy-ring ring-3"></div>
                    </div>
                  </div>

                  <div className="fusion-progress">
                    <div className="progress-bar">
                      <div className="progress-fill"></div>
                    </div>
                    <div className="progress-text">CHARGING REACTOR...</div>
                  </div>
                </div>

                {animationState === 'initiating' && (
                  <div className="fusion-indicator">
                    <div className="fusion-text">⚡ QUANTUM FUSION INITIALIZING...</div>
                    <div className="fusion-subtext">Building energy matrix...</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Prediction Result Popup */}
          {gameState.showPredictionResult && predictionResult && (
            <div className="quantum-prediction-popup-overlay">
              <div className="quantum-prediction-popup">
                <div className="prediction-header">
                  <h2>⚡ PREDICTION RESULT</h2>
                  <div className="prediction-info">
                    <span>Energy Analysis Complete</span>
                  </div>
                </div>

                <div className="prediction-display-zone">
                  {animationState === 'predicting' ? (
                    <div className="prediction-analyzing">
                      <div className="analyzing-text">⚡ ANALYZING ENERGY FIELD...</div>
                      <div className="analyzing-subtext">Quantum calculations in progress</div>
                    </div>
                  ) : (
                    <>
                      <div className="prediction-cards">
                        <div className="result-card">
                          <div className="card-label">PREVIOUS</div>
                          <div className="card-symbol">{getCardSymbol(gameState.session?.currentCard)}</div>
                          <div className="card-name">{CARD_NAMES[(gameState.session?.currentCard || 1) - 1]}</div>
                          <div className="card-value">{gameState.session?.currentCard}</div>
                        </div>
                        
                        <div className="prediction-arrow">→</div>
                        
                        <div className="result-card">
                          <div className="card-label">NEW</div>
                          <div className="card-symbol">{getCardSymbol(predictionResult.newCard)}</div>
                          <div className="card-name">{CARD_NAMES[predictionResult.newCard - 1]}</div>
                          <div className="card-value">{predictionResult.newCard}</div>
                        </div>
                      </div>

                      <div className={`prediction-result-display ${predictionResult.correct ? 'success' : 'failure'}`}>
                        <div className="result-icon">
                          {predictionResult.correct ? '✅' : '❌'}
                        </div>
                        <div className="result-title">
                          {predictionResult.correct ? 'FUSION SUCCESS!' : 'CORE MELTDOWN!'}
                        </div>
                        <div className="result-message">
                          {predictionResult.correct 
                            ? `Energy amplified to level ${predictionResult.streak}!`
                            : 'Reactor overloaded! Energy dispersed!'
                          }
                        </div>
                        {predictionResult.correct && (
                          <div className="streak-display">
                            🔥 STREAK: {predictionResult.streak}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Reactor Core Display - NEW HORIZONTAL LAYOUT */}
          <div className="reactor-core-zone">
            <div className="reactor-display-screen">
              <div className="display-header">
                <span>REACTOR STATUS</span>
                <span className={`reactor-status status-${getReactorStatus().toLowerCase()}`}>
                  {getReactorStatus()}
                </span>
              </div>
              
              <div className="reactor-core-container">
                <div className="reactor-core-horizontal">
                  {/* Compact Reactor Core */}
                  <div 
                    ref={reactorRef}
                    className={`compact-reactor-core ${gameState.session?.active ? 'active' : 'dormant'}`}
                    style={{
                      transform: `scale(${1 + (gameState.reactorPower / 100) * 0.2})`,
                      filter: `brightness(${1 + (gameState.reactorPower / 100)})`,
                    }}
                  >
                    <div className="core-center-compact">⚛️</div>
                    
                    {/* Compact Energy Rings */}
                    <div className="energy-rings-compact">
                      <div className="ring-1-compact" style={{ animationDuration: `${3 - gameState.reactorPower / 50}s` }}></div>
                      <div className="ring-2-compact" style={{ animationDuration: `${4 - gameState.reactorPower / 40}s` }}></div>
                    </div>
                  </div>

                  {/* Horizontal Energy Cell Display */}
                  {gameState.session?.active && (
                    <div className="energy-cell-horizontal">
                      <div className="cell-info-compact">
                        <div className="cell-symbol-compact">{getCardSymbol(gameState.session.currentCard)}</div>
                        <div className="cell-details">
                          <div className="cell-value-compact">{gameState.session.currentCard}</div>
                          <div className="cell-name-compact">{CARD_NAMES[gameState.session.currentCard - 1]}</div>
                        </div>
                      </div>
                      
                      <div className="energy-stats-compact">
                        <div className="stat-item-compact">
                          <span className="stat-label-compact">POWER LEVEL:</span>
                          <span className="stat-value-compact">{gameState.session.streak}</span>
                        </div>
                        <div className="stat-item-compact">
                          <span className="stat-label-compact">CURRENT ENERGY:</span>
                          <span className="stat-value-compact">{formatNumber(gameState.session.currentWinnings)} BLB</span>
                        </div>
                        <div className="stat-item-compact">
                          <span className="stat-label-compact">AMPLIFICATION:</span>
                          <span className="stat-value-compact">{gameStats.effectiveMultiplier}x</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Reactor Power Gauge */}
              <div className="power-gauge">
                <div className="gauge-label">REACTOR POWER</div>
                <div className="gauge-bar">
                  <div 
                    className="gauge-fill" 
                    style={{ 
                      width: `${gameState.reactorPower}%`,
                      background: `linear-gradient(90deg, #0080ff ${gameState.reactorPower}%, #ff6600 100%)`
                    }}
                  ></div>
                </div>
                <div className="gauge-value">{gameState.reactorPower}%</div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="quantum-controls">
            {/* Energy Input */}
            {!gameState.session?.active && (
              <div className="quantum-control-panel">
                <label className="quantum-control-label">⚡ ENERGY INPUT</label>
                <div>
                  <input
                    className="quantum-energy-input"
                    type="number"
                    value={gameState.betAmount}
                    onChange={(e) => setGameState(prev => ({ ...prev, betAmount: e.target.value }))}
                    disabled={gameState.isPlaying}
                    min="1"
                    max={gameState.balance}
                    step="1"
                    onFocus={() => playGameSound('quantumClick', 0.1)}
                  />
                  <div className="quantum-preset-buttons">
                    {['10', '50', '100'].map(amount => (
                      <button
                        key={amount}
                        onClick={() => {
                          playGameSound('quantumClick', 0.2);
                          setGameState(prev => ({ ...prev, betAmount: amount }));
                        }}
                        onMouseEnter={() => playGameSound('quantumHover', 0.1)}
                        disabled={gameState.isPlaying}
                        className="quantum-preset-btn"
                      >
                        {amount}
                      </button>
                    ))}
                    <button
                      onClick={() => {
                        playGameSound('quantumClick', 0.2);
                        setGameState(prev => ({ ...prev, betAmount: Math.floor(parseFloat(prev.balance)).toString() }));
                      }}
                      onMouseEnter={() => playGameSound('quantumHover', 0.1)}
                      disabled={gameState.isPlaying}
                      className="quantum-preset-btn max-btn"
                    >
                      MAX
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Prediction Controls */}
            {gameState.session?.active && (
              <div className="quantum-control-panel">
                <label className="quantum-control-label">⚡ ENERGY PREDICTION</label>
                <div className="prediction-grid">
                  {Object.values(PREDICTION_TYPES).map((prediction) => (
                    <button
                      key={prediction.id}
                      className="prediction-btn"
                      style={{
                        background: `linear-gradient(135deg, ${prediction.color}88, ${prediction.color}44)`,
                        borderColor: prediction.color,
                        boxShadow: `0 0 15px ${prediction.color}33`
                      }}
                      onClick={() => handleMakePrediction(prediction.id)}
                      onMouseEnter={() => playGameSound('quantumHover', 0.15)}
                      disabled={animationState === 'predicting'}
                    >
                      <div className="prediction-icon">{prediction.icon}</div>
                      <span className="prediction-name">{prediction.name}</span>
                      <span className="prediction-desc">{prediction.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="quantum-control-panel">
              {!gameState.session?.active ? (
                needsApproval() ? (
                  <button
                    className="quantum-action-btn quantum-approve-btn"
                    onClick={handleApprove}
                    onMouseEnter={() => playGameSound('quantumHover', 0.2)}
                    disabled={gameState.isApproving}
                  >
                    {gameState.isApproving ? '⚡ AUTHORIZING...' : '🔓 AUTHORIZE ENERGY'}
                  </button>
                ) : (
                  <button
                    className="quantum-action-btn quantum-fusion-btn"
                    onClick={handleInitiateFusion}
                    onMouseEnter={() => playGameSound('quantumHover', 0.2)}
                    disabled={!canInitiateFusion()}
                  >
                    {gameState.isPlaying ? '⚡ INITIALIZING...' : '⚛️ INITIATE FUSION'}
                  </button>
                )
              ) : (
                <button
                  className="quantum-action-btn quantum-harvest-btn"
                  onClick={handleHarvestEnergy}
                  onMouseEnter={() => playGameSound('quantumHover', 0.2)}
                  disabled={gameState.session?.streak === 0 || animationState === 'harvesting'}
                >
                  {animationState === 'harvesting' ? '⚡ HARVESTING...' : '🔋 HARVEST ENERGY'}
                </button>
              )}
            </div>

            {/* Potential Energy Display */}
            {gameState.session?.active && gameState.session.streak > 0 && (
              <div className="quantum-energy-display">
                ⚡ HARVESTABLE: {formatNumber(gameState.session.currentWinnings)} BLB
              </div>
            )}
          </div>
        </div>

        {/* Stats Sidebar */}
        <div className="quantum-stats-sidebar">
          <div className="quantum-stats-panel">
            <h3 className="quantum-stats-title">⚡ ENGINEER STATS</h3>
            <div className="quantum-stats-item">
              <span className="quantum-stats-label">Level</span>
              <span className="quantum-stats-value">{Math.floor(gameState.experience / 500) + 1}</span>
            </div>
            <div className="quantum-stats-item">
              <span className="quantum-stats-label">EXP</span>
              <span className="quantum-stats-value">{formatNumber(gameState.experience)}</span>
            </div>
            <div className="quantum-stats-item">
              <span className="quantum-stats-label">Wins</span>
              <span className="quantum-stats-value">{gameState.totalWins}</span>
            </div>
          </div>

          <div className="quantum-stats-panel">
            <h3 className="quantum-stats-title">🔬 REACTOR STATS</h3>
            <div className="quantum-stats-item">
              <span className="quantum-stats-label">Online</span>
              <span className="quantum-stats-value">{gameStats.playersOnline}</span>
            </div>
            <div className="quantum-stats-item">
              <span className="quantum-stats-label">Sessions</span>
              <span className="quantum-stats-value">{formatNumber(gameStats.totalGames)}</span>
            </div>
            <div className="quantum-stats-item">
              <span className="quantum-stats-label">Volume</span>
              <span className="quantum-stats-value">{formatNumber(gameStats.totalBets)}</span>
            </div>
          </div>

          <div className="quantum-stats-panel">
            <h3 className="quantum-stats-title">⚛️ FUSION GUIDE</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {[
                'Set energy input amount',
                'Initiate quantum fusion',
                'Predict energy levels',
                'Harvest amplified energy!'
              ].map((step, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ 
                    background: 'linear-gradient(45deg, #0080ff, #ff6600)', 
                    color: '#000', 
                    width: '2rem', 
                    height: '2rem', 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    fontWeight: '800', 
                    fontSize: '0.9rem', 
                    flexShrink: 0,
                    boxShadow: '0 0 8px rgba(0, 128, 255, 0.4)'
                  }}>{index + 1}</div>
                  <div style={{ color: 'rgba(0, 128, 255, 0.9)', fontWeight: '600', fontSize: '0.9rem' }}>
                    {step}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {gameHistory.length > 0 && (
            <div className="quantum-stats-panel">
              <h3 className="quantum-stats-title">📜 RECENT FUSIONS</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', maxHeight: '200px', overflowY: 'auto' }}>
                {gameHistory.map((game, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '0.8rem',
                    background: 'rgba(0, 128, 255, 0.03)',
                    borderRadius: '8px',
                    borderLeft: `3px solid ${game.won ? '#00ff80' : '#ff6600'}`,
                  }}>
                    <div style={{ fontSize: '1.5rem', flexShrink: 0 }}>
                      {getCardSymbol(game.initialCard)}
                    </div>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ color: 'rgba(0, 128, 255, 0.7)', fontSize: '0.8rem', fontWeight: '600' }}>
                          {game.cashedOut ? 'HARVESTED' : (game.won ? 'SUCCESS' : 'MELTDOWN')}
                        </div>
                        <div style={{ 
                          color: game.won ? '#00ff80' : '#ff6600', 
                          fontWeight: '800', 
                          fontSize: '0.9rem'
                        }}>
                          {game.won ? `+${formatNumber(game.netPayout)}` : 'LOSS'}
                        </div>
                      </div>
                      <div style={{ color: 'rgba(0, 128, 255, 0.5)', fontSize: '0.7rem' }}>
                        Streak: {game.streak} | Bet: {formatNumber(game.betAmount)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HiLoCards;